// src/firebase/authorization/authorizedUsersService.ts
import {
	getAuthorizedUsers,
	isUserAuthorized,
	addAuthorizedUser,
	updateAuthorizedUser,
	deactivateUser,
	removeAuthorizedUser,
	getUserRole,
	AuthorizedUser
} from "./authorizedUsersRepository";

/**
 * Service for managing authorized users
 * This service provides business logic for authorization management
 */

export class AuthorizedUsersService {
	/**
	 * Check if a user is authorized
	 */
	static async checkAuthorization(email: string): Promise<boolean> {
		return isUserAuthorized(email);
	}

	/**
	 * Get user role
	 */
	static async getUserRole(email: string): Promise<"admin" | "user" | null> {
		return getUserRole(email);
	}

	/**
	 * Add a new authorized user
	 */
	static async addUser(
		email: string,
		displayName: string,
		role: "admin" | "user" = "user",
		isPartner = false
	): Promise<boolean> {
		const newUser: AuthorizedUser = {
			email,
			displayName,
			role,
			createdAt: new Date().toISOString(),
			isActive: true,
			isPartner
		};

		return addAuthorizedUser(newUser);
	}

	/**
	 * Deactivate a user (soft delete)
	 */
	static async deactivateUser(email: string): Promise<boolean> {
		return deactivateUser(email);
	}

	/**
	 * Reactivate a user
	 */
	static async reactivateUser(email: string): Promise<boolean> {
		return updateAuthorizedUser(email, { isActive: true });
	}

	/**
	 * Change user role
	 */
	static async changeUserRole(
		email: string,
		role: "admin" | "user"
	): Promise<boolean> {
		return updateAuthorizedUser(email, { role });
	}

	/**
	 * Delete a user completely
	 */
	static async deleteUser(email: string): Promise<boolean> {
		return removeAuthorizedUser(email);
	}

	/**
	 * Get all authorized users
	 */
	static async getAllUsers(): Promise<Record<string, AuthorizedUser>> {
		return getAuthorizedUsers();
	}

	/**
	 * Get active users only
	 */
	static async getActiveUsers(): Promise<AuthorizedUser[]> {
		const users = await getAuthorizedUsers();
		return Object.values(users).filter((user) => user.isActive);
	}

	/**
	 * Get active partner users only
	 */
	static async getActivePartners(): Promise<AuthorizedUser[]> {
		const users = await getAuthorizedUsers();
		return Object.values(users).filter(
			(user) => user.isActive && user.isPartner === true
		);
	}

	/**
	 * Get inactive users
	 */
	static async getInactiveUsers(): Promise<AuthorizedUser[]> {
		const users = await getAuthorizedUsers();
		return Object.values(users).filter((user) => !user.isActive);
	}

	/**
	 * Get admin users
	 */
	static async getAdmins(): Promise<AuthorizedUser[]> {
		const users = await getAuthorizedUsers();
		return Object.values(users).filter((user) => user.role === "admin");
	}

	/**
	 * Update user (remove and add pattern)
	 */
	static async updateUser(
		email: string,
		updates: Partial<AuthorizedUser>
	): Promise<boolean> {
		return updateAuthorizedUser(email, updates);
	}

	/**
	 * Remove/delete user
	 */
	static async removeUser(email: string): Promise<boolean> {
		return removeAuthorizedUser(email);
	}
}

// Create and export a singleton instance
export const authorizedUsersService = AuthorizedUsersService;
